import { useMemo } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import type { LiveLessonCompetency } from '../../lib/liveLessonTypes';
import CollapsibleTile from './CollapsibleTile';

type LessonCompetencyPanelProps = {
  competencies: LiveLessonCompetency[];
  activeIds: number[];
  suggestedIds: number[];
  loading?: boolean;
  suggesting?: boolean;
  suggestSource?: 'llm' | 'keywords' | 'none' | null;
  onToggle: (competencyId: number) => void;
};

export default function LessonCompetencyPanel({
  competencies,
  activeIds,
  suggestedIds,
  loading = false,
  suggesting = false,
  suggestSource,
  onToggle
}: LessonCompetencyPanelProps) {
  const activeSet = new Set(activeIds);
  const suggestedSet = new Set(suggestedIds);

  const sortedCompetencies = useMemo(() => {
    const activeOrder = new Map(activeIds.map((id, index) => [id, index]));
    const activeLookup = new Set(activeIds);
    const suggestedLookup = new Set(suggestedIds);
    return [...competencies].sort((a, b) => {
      const aActive = activeLookup.has(a.id);
      const bActive = activeLookup.has(b.id);
      if (aActive !== bActive) return aActive ? -1 : 1;
      if (aActive && bActive) {
        const orderDiff = (activeOrder.get(a.id) ?? 0) - (activeOrder.get(b.id) ?? 0);
        if (orderDiff !== 0) return orderDiff;
      }
      const aSuggested = suggestedLookup.has(a.id);
      const bSuggested = suggestedLookup.has(b.id);
      if (aSuggested !== bSuggested) return aSuggested ? -1 : 1;
      return a.name.localeCompare(b.name, 'de');
    });
  }, [competencies, activeIds, suggestedIds]);

  const title =
    competencies.length > 0
      ? `Kompetenzen ${activeIds.length}/${competencies.length}`
      : 'Kompetenzen';

  return (
    <CollapsibleTile title={title} defaultOpen contentClassName="space-y-2">
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Kompetenzen werden geladen…
        </div>
      ) : competencies.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {suggestSource === 'none' && !loading
            ? 'Kein Curriculum für das gewählte Fach gefunden.'
            : 'Bitte Fach wählen, um Kompetenzen zu laden.'}
        </p>
      ) : (
        <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
          {sortedCompetencies.map((competency) => {
            const isActive = activeSet.has(competency.id);
            const isSuggested = suggestedSet.has(competency.id);
            return (
              <button
                key={competency.id}
                type="button"
                onClick={() => onToggle(competency.id)}
                className={clsx(
                  'flex w-full items-start gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors',
                  isActive &&
                    'border-primary-500 bg-primary-600 text-white dark:border-primary-400 dark:bg-primary-600',
                  !isActive &&
                    isSuggested &&
                    'border-primary-300 bg-primary-50 text-gray-900 dark:border-primary-500/60 dark:bg-primary-950/50 dark:text-gray-100',
                  !isActive &&
                    !isSuggested &&
                    'border-gray-200 bg-white text-gray-800 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                )}
              >
                {isSuggested && !isActive ? (
                  <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary-500 dark:text-primary-400" />
                ) : (
                  <span
                    className={clsx(
                      'mt-1 h-2 w-2 shrink-0 rounded-full',
                      isActive ? 'bg-white' : 'bg-gray-300 dark:bg-gray-600'
                    )}
                    aria-hidden
                  />
                )}
                <span className="min-w-0 flex-1 font-medium leading-snug">{competency.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </CollapsibleTile>
  );
}
